import * as BABYLON from "babylonjs";
import * as _ from "lodash";
import * as React from "react";
import * as ReactDOM from "react-dom";
import { EarthCamera } from "../../earth/display/earthCamera";
import { EarthEngine } from "../../earth/earthEngine";
import { FrameTime } from "../../earth/earthEngine";
import { Country } from "../../earth/model/country";
import { Land, LandLevel } from "../../earth/model/land";
import { Main } from "../main";
import { Brand } from "../model/brand";
import { MetricType } from "../model/metricType";
import { Publication } from "../model/publication";
import { AppTitle } from "./appTitle";
import { BrandSelector } from "./brandSelector";
import { CountryRanking } from "./countryRanking";
import { DebugButton } from "./debugButton";
import { InfoPanel } from "./infoPanel";
import { MetricsChart } from "./metricsChart";
import { MetricSelector } from "./metricSelector";
import { PieChartManager } from "./pieChartManager";
import { PublicationRanking } from "./publicationRanking";
import { UIContext } from "./uiContext";
import { UIContextDetails } from "./uiContextDetails";
import { UIContextLandDetails } from "./uiContextLandDetails";
import { UIContextLoadingScreen } from "./uiContextLoadingScreen";
import { UIContextNavigation } from "./uiContextNavigation";
import { UIGlobalState } from "./uiGlobalState";
import { YoutubePlayer } from "./youtubePlayer";

class UIManagerState extends UIGlobalState {
    displayedContext = new UIContext();
    infoPanelVisible = false;
}

export class UIManager extends React.Component<{}, UIManagerState> {
    pieChartManager: PieChartManager | null = null;
    currentContext: UIContext;

    get landLevel(): LandLevel {
        return this.currentContext.landLevel;
    }

    constructor(props: {}) {
        super(props);

        this.state = new UIManagerState();
        this.currentContext = new UIContext();

        this.onSelectedBrandsChanged = this.onSelectedBrandsChanged.bind(this);
        this.onSelectedMetricTypeChanged = this.onSelectedMetricTypeChanged.bind(this);
        this.onCountrySelectedWithRanking = this.onCountrySelectedWithRanking.bind(this);
        this.onInfoButtonClicked = this.onInfoButtonClicked.bind(this);
        this.onInfoCloseButtonClicked = this.onInfoCloseButtonClicked.bind(this);
    }

    switchToContext(context: UIContext) {
        if (this.currentContext) {
            this.currentContext.leave(context);
        }

        context.shouldSwitchToContext.connect(ev => { this.switchToContext(ev); });
        context.enter(this.currentContext);

        this.currentContext = context;
        this.setState({ displayedContext: context });
    }

    componentDidMount() {
        this.switchToContext(new UIContextLoadingScreen());
    }

    update(time: FrameTime) {
        this.currentContext.update(time);

        if (this.pieChartManager) {
            this.pieChartManager.update(time);
        }

        this.setState({
            landLevel: this.currentContext.landLevel,
            selectedCountry: this.currentContext.selectedCountry
        });
    }

    render(): JSX.Element {
        const opts = {
            height: "390",
            width: "640",
            playerVars: { // https://developers.google.com/youtube/player_parameters
              autoplay: 1
            }
          };
        return <div className="fullscreen">
            <PieChartManager ref={ref => { this.pieChartManager = ref; }}
                             landLevel={this.state.landLevel}
                             selectedMetricType={this.state.selectedMetricType}
                             selectedBrands={this.state.selectedBrands}
                             selectedCountry={this.state.selectedCountry}/>

            <div id="uiOverlay" className="fullscreen">
                <div className="fullscreen hbox">
                    <div className="vcenter" style={{ marginLeft: "20px" }}>
                        <BrandSelector selectedBrands={this.state.selectedBrands}
                                       multipleSelectionEnabled={this.isMultiBrandSelectionAllowed(this.state.selectedMetricType)}
                                       onSelectedBrandsChanged={this.onSelectedBrandsChanged}/>
                    </div>
                </div>

                <div className="fullscreen hbox">
                    <div className="expand"/>
                    <div className="vcenter" style={{ marginRight: 10 }}>
                        <MetricSelector selectedMetricType={this.state.selectedMetricType}
                                        onSelectedMetricTypeChanged={this.onSelectedMetricTypeChanged}/>
                    </div>
                </div>

                <div className="fullscreen hbox">
                    <div className="expand"/>
                    <div className="top" style={{ marginTop: 20, marginRight: 20 }}>
                        <PublicationRanking onPublicationSelected={this.onPublicationSelectedWithinRanking} visible={this.state.selectedCountry == null}/>
                    </div>
                </div>

                <div className="fullscreen hbox video">
                <div className="expand"/>
                <div className="vbox right-aligned" style={{ marginBottom: "20px", marginRight: "20px" }}>
                <div className="expand"/>
                        <YoutubePlayer list="PLL1yQ7yJtUSaMhEg89WtGiOBxrx-nw5Ep" visible={this.state.selectedCountry == null}/>
                </div>
                </div>

                <div className="fullscreen vbox">
                    <div className="expand"/>
                    <div className="hcenter">
                        <MetricsChart selectedMetricType={this.state.selectedMetricType}
                                      selectedBrands={this.state.selectedBrands}
                                      selectedCountry={this.state.selectedCountry}/>
                    </div>
                </div>

                {this.state.displayedContext.render(this.state)}

                <div style={{ position: "absolute", left: 20, top: 20 }}>
                    <AppTitle onInfoButtonClicked={this.onInfoButtonClicked}/>
                </div>
            </div>

            <InfoPanel visible={this.state.infoPanelVisible} onCloseButtonClicked={this.onInfoCloseButtonClicked}/>

            {/*<div style={{ position: "absolute", left: 10, bottom: 10 }}>
                <DebugButton/>
            </div>*/}
        </div>;
    }

    static renderToElement(rootElement: string): UIManager {
        return ReactDOM.render(<UIManager/>, document.getElementById(rootElement)) as UIManager;
    }

    private isMultiBrandSelectionAllowed(metricType: MetricType) {
        return !metricType.hasSubMetrics;
    }

    private updateSelectedBrandsOnMetricTypeChanged(metricType: MetricType) {
        if (this.isMultiBrandSelectionAllowed(metricType)) {
            if (!this.isMultiBrandSelectionAllowed(this.state.selectedMetricType)) {
                this.setState({ selectedBrands: Brand.AllBrands });
            }
        } else if (this.state.selectedBrands.length > 1) {
            this.setState({ selectedBrands: _.take(this.state.selectedBrands, 1) });
        }
    }

    private onSelectedBrandsChanged(selectedBrands: Brand[]) {
        this.setState({ selectedBrands });
    }

    private onSelectedMetricTypeChanged(metricType: MetricType) {
        this.updateSelectedBrandsOnMetricTypeChanged(metricType);
        this.setState({ selectedMetricType:  metricType });
    }

    private onCountrySelectedWithRanking(country: Country) {
        this.switchToContext(new UIContextDetails(country));
    }

    private onInfoButtonClicked() {
        this.setState({ infoPanelVisible: true });
    }

    private onInfoCloseButtonClicked() {
        this.setState({ infoPanelVisible: false });
    }

    onTap(tap: BABYLON.PointerInfo): boolean { // return true to block inputs
        if (this.pieChartManager) {
            let pie = this.pieChartManager.getPieChartAtCoord(tap.event.x, tap.event.y);
            if (pie) {
                if (pie.props.land instanceof Country) {
                    this.switchToContext(new UIContextDetails(pie.props.land));
                } else {
                    EarthEngine.instance.interactionManager.focusCameraOnLand(pie.props.land);
                    this.switchToContext(new UIContextLandDetails(pie.props.land));
                }
                return true;
            }
        }
        return false;
    }

    refresh() {
        this.setState({});
    }

    private onPublicationSelectedWithinRanking(publication: Publication) {
        let win = window.open(publication.linkURL, "_blank");
        win!.focus();
    }

    private onVideoReady(event: Event) {
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--1!./src/app/ui/uiManager.tsx